import React, { useEffect, useState } from "react";
import {
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    TextField,
    Divider,
    Box,
    Typography,
    IconButton,
    Card,
    CardContent,
    Avatar,
    Stack,
} from "@mui/material";
import { Email, Phone, AccountCircle, Edit } from "@mui/icons-material";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import XIcon from "@mui/icons-material/X";
import CustomDialogWithContent from "../../../components/global/CustomDialogWithContent";
import { validateForm } from "../../../components/booking/ConfirmAppointment";
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import ImageSelect from "../../../components/global/images/ImageSelector";
import ImageViewer from "../../../components/global/images/imageViewer";
import { EMPLOYEE_BIO_MAX_LENGTH } from "../../../constants";

interface ContactCardProps {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    bio?: string;
    socials?: {
        instagram?: string;
        facebook?: string;
        twitter?: string;
    };
    profileImagePath?: string;
    profileImageRevision?: number;
    onSave: (
        firstName: string,
        lastName: string,
        email: string,
        phone: string,
        bio: string,
        socials: { instagram?: string; facebook?: string; twitter?: string },
        imageFile: File | undefined,
        removeImage: boolean
    ) => Promise<void>;
    isSaving?: boolean;
}

const ContactCard: React.FC<ContactCardProps> = ({
    firstName,
    lastName,
    email,
    phone,
    bio,
    socials,
    profileImagePath,
    profileImageRevision,
    onSave,
    isSaving = false,
}) => {
    const normalizeSocialHandle = (value: string) => {
        const trimmed = value.trim();
        if (!trimmed) return '';
        const withoutProtocol = trimmed.replace(/^https?:\/\//i, '');
        const withoutDomain = withoutProtocol
            .replace(/^www\./i, '')
            .replace(/^instagram\.com\//i, '')
            .replace(/^x\.com\//i, '')
            .replace(/^twitter\.com\//i, '');
        return withoutDomain.replace(/^@+/, '').split('/')[0].split('?')[0].trim();
    };

    const normalizeFacebookDisplay = (value: string) => {
        const trimmed = value.trim();
        if (!trimmed) return '';
        const withoutProtocol = trimmed.replace(/^https?:\/\//i, '');
        const withoutDomain = withoutProtocol
            .replace(/^www\./i, '')
            .replace(/^facebook\.com\//i, '')
            .replace(/^fb\.com\//i, '');
        const token = withoutDomain.split('/')[0].split('?')[0].trim();
        return token || trimmed;
    };

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editedEmail, setEditedEmail] = useState(email);
    const [editedPhone, setEditedPhone] = useState(phone || "");
    const [editedFirstName, setEditedFirstName] = useState(firstName || "");
    const [editedLastName, setEditedLastName] = useState(lastName || "");
    const [editedBio, setEditedBio] = useState(bio || "");
    const [editedInstagram, setEditedInstagram] = useState(normalizeSocialHandle(socials?.instagram || ""));
    const [editedFacebook, setEditedFacebook] = useState(socials?.facebook || "");
    const [editedTwitter, setEditedTwitter] = useState(normalizeSocialHandle(socials?.twitter || ""));
    const [stagedImage, setStagedImage] = useState<File | undefined>();
    const [removeImage, setRemoveImage] = useState(false);
    const [imageEditorKey, setImageEditorKey] = useState(0);

    useEffect(() => {
        setEditedEmail(email || "");
        setEditedPhone(phone || "");
        setEditedFirstName(firstName || "");
        setEditedLastName(lastName || "");
        setEditedBio((bio || "").slice(0, EMPLOYEE_BIO_MAX_LENGTH));
        setEditedInstagram(normalizeSocialHandle(socials?.instagram || ""));
        setEditedFacebook(socials?.facebook || "");
        setEditedTwitter(normalizeSocialHandle(socials?.twitter || ""));
    }, [firstName, lastName, email, phone, bio, socials?.instagram, socials?.facebook, socials?.twitter]);

    // Error messages
    const [errors, setErrors] = useState<{ firstName?: string; lastName?: string; email?: string; phone?: string; }>({});

    const resetStagedImage = () => {
        setStagedImage(undefined);
        setRemoveImage(false);
        setImageEditorKey((current) => current + 1);
    };

    const openEditor = () => {
        setEditedEmail(email || "");
        setEditedPhone(phone || "");
        setEditedFirstName(firstName || "");
        setEditedLastName(lastName || "");
        setEditedBio((bio || "").slice(0, EMPLOYEE_BIO_MAX_LENGTH));
        setEditedInstagram(normalizeSocialHandle(socials?.instagram || ""));
        setEditedFacebook(socials?.facebook || "");
        setEditedTwitter(normalizeSocialHandle(socials?.twitter || ""));
        setErrors({});
        resetStagedImage();
        setIsDialogOpen(true);
    };

    const cancelEditor = () => {
        if (isSaving) return;
        resetStagedImage();
        setIsDialogOpen(false);
    };

    const handleImageChange = (newPicture?: File) => {
        setStagedImage(newPicture);
        setRemoveImage(!newPicture && Boolean(profileImagePath));
    };

    const handleOnSave = async (trimmedFirstName: string, trimmedLastName: string, trimmedEmail: string, trimmedPhone: string) => {
        const newErrors = validateForm(trimmedFirstName, trimmedLastName, trimmedEmail, trimmedPhone);
        setErrors(newErrors);
      
        if (Object.keys(newErrors).length > 0) {
          return;
        }
        else {
            try {
                await onSave(trimmedFirstName, trimmedLastName, trimmedEmail, trimmedPhone, editedBio.slice(0, EMPLOYEE_BIO_MAX_LENGTH), {
                    instagram: normalizeSocialHandle(editedInstagram) || undefined,
                    facebook: editedFacebook.trim() || undefined,
                    twitter: normalizeSocialHandle(editedTwitter) || undefined,
                }, stagedImage, removeImage);
                resetStagedImage();
                setIsDialogOpen(false);
            } catch {
                // Keep the editor and staged image open so the save can be retried.
            }
        }
    }

    return (
        <>
            <Box sx={{ width: "100%", maxWidth: 560, mx: "auto" }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ bgcolor: "grey.100", px: 2, py: 1, borderRadius: 0.5 }}>
                    <Typography variant="h6" fontWeight={700}>
                        Your Information
                    </Typography>
                    <IconButton size="small" onClick={openEditor} aria-label="Edit information">
                        <Edit fontSize="small" />
                    </IconButton>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Card
                    elevation={2}
                    sx={{
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                        overflow: "hidden",
                    }}
                >
                    <CardContent sx={{ '&:last-child': { pb: 3 } }}>
                        <Box sx={{ mb: 2 }}>
                        <ImageViewer
                            imagePath={profileImagePath}
                            refreshKey={profileImageRevision}
                            styleType="portrait"
                            altText="Profile Image"
                            placeholderComponent={
                                <Box
                                    sx={{
                                        width: "100%",
                                        aspectRatio: "3 / 4",
                                        borderRadius: 1,
                                        bgcolor: "grey.100",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        }}
                                    >
                                        <Avatar sx={{ width: 72, height: 72 }} />
                                    </Box>
                                }
                            />
                        </Box>
                        <List dense>
                            <ListItem disableGutters>
                                <ListItemIcon sx={{ minWidth: 36, color: "text.secondary" }}>
                                    <AccountCircle />
                                </ListItemIcon>
                                <ListItemText
                                    primary={`${firstName} ${lastName}`}
                                    primaryTypographyProps={{ fontWeight: 600, fontSize: '1rem' }}
                                />
                            </ListItem>

                            <ListItem disableGutters>
                                <ListItemIcon sx={{ minWidth: 36, color: "text.secondary" }}>
                                    <Phone />
                                </ListItemIcon>
                                <ListItemText
                                    primary={phone || "—"}
                                    primaryTypographyProps={{ fontWeight: 600, fontSize: '1rem' }}
                                />
                            </ListItem>

                            <ListItem disableGutters>
                                <ListItemIcon sx={{ minWidth: 36, color: "text.secondary" }}>
                                    <Email />
                                </ListItemIcon>
                                <ListItemText
                                    primary={email}
                                    primaryTypographyProps={{ fontWeight: 600, fontSize: '1rem' }}
                                />
                            </ListItem>

                            <ListItem disableGutters>
                                <ListItemText
                                    primary="Your Bio"
                                    secondary={bio?.trim() ? bio : "—"}
                                    primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }}
                                    secondaryTypographyProps={{ color: "text.secondary" }}
                                />
                            </ListItem>

                            <ListItem disableGutters>
                                <ListItemText
                                    primary="Social Links"
                                    secondary={
                                        <Box component="span" sx={{ display: "block" }}>
                                            {socials?.instagram && (
                                                <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                                    <InstagramIcon sx={{ fontSize: 20 }} />
                                                    {`@${normalizeSocialHandle(socials.instagram)}`}
                                                </Box>
                                            )}
                                            {socials?.facebook && (
                                                <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                                    <FacebookIcon sx={{ fontSize: 20 }} />
                                                    {normalizeFacebookDisplay(socials.facebook)}
                                                </Box>
                                            )}
                                            {socials?.twitter && (
                                                <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                                    <XIcon sx={{ fontSize: 20 }} />
                                                    {`@${normalizeSocialHandle(socials.twitter)}`}
                                                </Box>
                                            )}
                                            {!socials?.instagram && !socials?.facebook && !socials?.twitter && "—"}
                                        </Box>
                                    }
                                    primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }}
                                    secondaryTypographyProps={{ color: "text.secondary" }}
                                />
                            </ListItem>
                        </List>
                    </CardContent>
                </Card>
            </Box>

            {/* Edit profile dialog */}
            <CustomDialogWithContent
                open={isDialogOpen}
                title="Edit Your Profile"
                fullScreenOnMobile
                stickyActions
                actionButtonVariant="contained"
                actionButtonColor="inherit"
                actionButtonSx={{ bgcolor: 'common.black', color: 'common.white', '&:hover': { bgcolor: 'grey.900' } }}
                content={
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <ImageSelect
                                key={imageEditorKey}
                                initialImagePath={profileImagePath}
                                allowDelete={true}
                                enabled={!isSaving}
                                styleType="circle"
                                onImageChange={handleImageChange}
                                altText="Profile Image"
                                placeholderComponent={<Avatar sx={{ width: 120, height: 120 }} />}
                            />
                        </Box>
                        <TextField
                            label="First Name"
                            fullWidth
                            value={editedFirstName}
                            margin="dense"
                            onChange={(e) => setEditedFirstName(e.target.value)}
                            error={Boolean(errors.firstName)}
                            helperText={errors.firstName}
                        />
                        <TextField
                            label="Last Name"
                            fullWidth
                            value={editedLastName}
                            margin="dense"
                            onChange={(e) => setEditedLastName(e.target.value)}
                            error={Boolean(errors.lastName)}
                            helperText={errors.lastName}
                        />
                        <style>{`
                            .PhoneInput {
                                width: 100% !important;
                            }
                            .PhoneInputInput {
                                height: 56px !important;
                                padding: 10.5px 14px !important;
                                font-size: 16px !important;
                                border-radius: 4px !important;
                                border: 1px solid rgba(0, 0, 0, 0.23) !important;
                                box-sizing: border-box !important;
                                margin: 0 auto !important;
                            }
                        `}</style>
                    <PhoneInput
                        defaultCountry="US"
                        countries={['US', 'CA']} 
                        international={true}
                        addInternationalOption={false} 
                        value={editedPhone}
                        onChange={(value) => setEditedPhone(value || '')}
                        className="PhoneInput"
                        inputStyle={{
                            border: 'none',
                            outline: 'none',
                            width: '100%',
                            fontSize: '16px',
                            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                        }}
                    />
                    {Boolean(errors.phone) && (
                        <>
                            <style>{`
                            .PhoneInputInput {
                                border: 1px solid #ff1744 !important;
                                margin-bottom: 0 !important;
                            }
                            `}</style>
                            <div style={{ color: '#ff1744', fontSize: '0.75rem', marginTop: -13, marginLeft: 14, width: '80%'}}>
                            {errors.phone}
                            </div>
                        </>
                    )}
                        <TextField
                            label="Email"
                            fullWidth
                            value={editedEmail}
                            disabled={true}
                            margin="dense"
                            onChange={(e) => setEditedEmail(e.target.value)}
                            error={Boolean(errors.email)}
                            helperText={errors.email}
                        />
                        <TextField
                            label="Bio"
                            fullWidth
                            multiline
                            minRows={3}
                            value={editedBio}
                            margin="dense"
                            onChange={(e) => setEditedBio(e.target.value.slice(0, EMPLOYEE_BIO_MAX_LENGTH))}
                            helperText={`${editedBio.length}/${EMPLOYEE_BIO_MAX_LENGTH}`}
                            inputProps={{ maxLength: EMPLOYEE_BIO_MAX_LENGTH }}
                        />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                label="Instagram Username"
                                fullWidth
                                margin="dense"
                                value={editedInstagram}
                                onChange={(e) => setEditedInstagram(e.target.value)}
                                placeholder="username"
                                helperText="Enter username only (without @)."
                            />
                            <TextField
                                label="Facebook URL"
                                fullWidth
                                margin="dense"
                                value={editedFacebook}
                                onChange={(e) => setEditedFacebook(e.target.value)}
                                placeholder="https://facebook.com/username"
                            />
                        </Stack>
                        <TextField
                            label="Twitter / X Username"
                            fullWidth
                            margin="dense"
                            value={editedTwitter}
                            onChange={(e) => setEditedTwitter(e.target.value)}
                            placeholder="username"
                            helperText="Enter username only (without @)."
                        />
                    </Box>
                }
                actionButtonText="Save"
                isLoading={isSaving}
                onAction={() => {
                    void handleOnSave(editedFirstName.trim(), editedLastName.trim(), editedEmail.trim(), editedPhone);
                }}
                onCancel={cancelEditor}
            />
        </>
    );
};

export default ContactCard;
